/**
 * 标签页状态管理
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import TabsItem from '@/api'
import { RouteLocationNormalized } from 'vue-router'

export const useTabsStore = defineStore('tabs', {
  state: () => ({
    visitedViews: [] as TagView[],
    cachedViews: [] as string[],
    isInitialized: false,
  }),
  getters: {
    // 检查是否是首页（增强版）
    isHomeView: () => (route: RouteLocationNormalized | TagView) => {
      // 优先检查 meta.isHome
      if (route.meta?.isHome === true) return true
      const homeName = import.meta.env.VITE_HOME_PAGE || 'dashboard'
      const homePath = `/layout/${homeName}`
      let routePath = ''
      if ('fullPath' in route && route.fullPath) {
        routePath = route.fullPath
      } else if ('path' in route && route.path) {
        routePath = route.path
      }
      // 提取基础路径
      const getBasePath = (path: string) => {
        if (!path) return ''
        return path.split('?')[0].split('#')[0].replace(/\/+$/, '').toLowerCase()
      }
      const normalizedRoutePath = getBasePath(routePath)
      const normalizedHomePath = getBasePath(homePath)
      // 路径匹配
      if (normalizedRoutePath === normalizedHomePath) return true
      // 路由名称匹配
      if (route.name === 'Dashboard') return true
      // 标题匹配
      if (route.meta?.title === '课时概况') return true
      return false
    },
    // 获取当前激活的标签页
    activeTab: (state) => (path: string) => {
      return state.visitedViews.find(v =>
        state.normalizePath(v.path) === state.normalizePath(path)
      )
    },
    // 获取标签页索引
    tabIndex: (state) => (path: string) => {
      return state.visitedViews.findIndex(v =>
        state.normalizePath(v.path) === state.normalizePath(path)
      )
    }
  },
  actions: {
    // 路径标准化处理
    normalizePath(path: string): string {
      if (!path) return ''
      return path.replace(/\/+$/, '').toLowerCase()
    },

    // 强制清理所有首页标签
    cleanAllHomeTabs() {
      const beforeCount = this.visitedViews.length
      this.visitedViews = this.visitedViews.filter(view => !this.isHomeView(view))
      const afterCount = this.visitedViews.length
      if (beforeCount !== afterCount) this.saveToLocalStorage()
    },

    // 初始化标签页
    initTagsView() {
      if (this.isInitialized) return
      try {
        const visitedViews = sessionStorage.getItem('visitedViews')
        const cachedViews = sessionStorage.getItem('cachedViews')
        if (visitedViews) {
          const parsed = JSON.parse(visitedViews)
          // 关键：过滤掉首页
          this.visitedViews = Array.isArray(parsed)
            ? parsed.filter((view: any) => !this.isHomeView(view))
            : []
        }
        if (cachedViews) {
          const parsed = JSON.parse(cachedViews)
          this.cachedViews = Array.isArray(parsed) ? parsed : []
        }
        this.isInitialized = true
      } catch (error) {
        this.visitedViews = []
        this.cachedViews = []
        this.isInitialized = true
      }
    },

    // 添加标签页
    addView(view: RouteLocationNormalized) {
      if (!this.isInitialized) this.initTagsView()
      // 如果是首页，坚决不添加
      if (this.isHomeView(view)) return
      this.addVisitedView(view)
      this.addCachedView(view)
    },

    // 添加访问过的视图
    addVisitedView(view: RouteLocationNormalized) {
      const tagView = {
        path: view.fullPath || view.path,
        name: view.name,
        meta: {
          title: view.meta?.title,
          icon: view.meta?.icon,
          ...view.meta
        },
      }
      // 再次检查是否为首页
      if (this.isHomeView(tagView)) return
      // 检查是否已存在
      const exists = this.visitedViews.some(v =>
        this.normalizePath(v.path) === this.normalizePath(tagView.path)
      )
      if (!exists) {
        this.visitedViews.push(tagView)
        this.saveToLocalStorage()
      }
    },

    // 添加缓存视图
    addCachedView(view: RouteLocationNormalized) {
      const name = view.name as string
      if (name && !this.cachedViews.includes(name)) {
        this.cachedViews.push(name)
        this.saveToLocalStorage()
      }
    },

    // 删除标签页
    delView(view: TagView | string) {
      const path = typeof view === 'string' ? view : view.path
      const normalizedPath = this.normalizePath(path)
      const viewToDelete = this.visitedViews.find(v =>
        this.normalizePath(v.path) === normalizedPath
      )
      if (viewToDelete) {
        this.visitedViews = this.visitedViews.filter(v =>
          this.normalizePath(v.path) !== normalizedPath
        )
        if (viewToDelete.name) {
          const name = viewToDelete.name as string
          this.cachedViews = this.cachedViews.filter(n => n !== name)
        }
        this.saveToLocalStorage()
      }
    },

    // 删除其他标签页
    delOthersViews(view: TagView | string) {
      const path = typeof view === 'string' ? view : view.path
      const normalizedPath = this.normalizePath(path)
      const currentView = this.visitedViews.find(v =>
        this.normalizePath(v.fullPath || v.path) === normalizedPath
      )
      if (currentView) {
        this.visitedViews = [currentView]
        this.cachedViews = this.cachedViews.filter(name =>
          name === (currentView.name as string)
        )
        this.saveToLocalStorage()
      }
    },

    // 删除所有标签页（不保留任何标签）
    delAllViews() {
      // 修改：清空所有，不保留首页
      this.visitedViews = []
      this.cachedViews = []
      this.saveToLocalStorage()
    },

    // 关闭左侧标签页
    delLeftViews(view: TagView | string) {
      const path = typeof view === 'string' ? view : view.path
      const index = this.visitedViews.findIndex(v =>
        this.normalizePath(v.path) === this.normalizePath(path)
      )
      if (index > 0) {
        const viewsToDelete = this.visitedViews.slice(0, index)
        this.visitedViews = this.visitedViews.slice(index)
        const namesToDelete = viewsToDelete.map(v => v.name as string).filter(Boolean)
        this.cachedViews = this.cachedViews.filter(name => !namesToDelete.includes(name))
        this.saveToLocalStorage()
      }
    },

    // 关闭右侧标签页
    delRightViews(view: TagView | string) {
      const path = typeof view === 'string' ? view : view.path
      const index = this.visitedViews.findIndex(v =>
        this.normalizePath(v.path) === this.normalizePath(path)
      )
      if (index < this.visitedViews.length - 1) {
        const viewsToDelete = this.visitedViews.slice(index + 1)
        this.visitedViews = this.visitedViews.slice(0, index + 1)
        const namesToDelete = viewsToDelete.map(v => v.name as string).filter(Boolean)
        this.cachedViews = this.cachedViews.filter(name => !namesToDelete.includes(name))
        this.saveToLocalStorage()
      }
    },

    // 保存到本地存储
    saveToLocalStorage() {
      try {
        sessionStorage.setItem('visitedViews', JSON.stringify(this.visitedViews))
        sessionStorage.setItem('cachedViews', JSON.stringify(this.cachedViews))
      } catch (error) {
        console.error('保存标签页数据失败:', error)
      }
    },

    // 清空标签页（彻底重置）
    clearTabs() {
      this.visitedViews = []
      this.cachedViews = []
      this.isInitialized = false
      sessionStorage.removeItem('visitedViews')
      sessionStorage.removeItem('cachedViews')
    },

    // 重置并重新初始化
    resetTabs() {
      this.clearTabs()
      this.initTagsView()
    }
  }
})
